export interface ApiClientOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  headers?: Record<string, string>;
  body?: unknown;
  signal?: AbortSignal;
}

// Use relative URLs for consolidated Pages deployment
// Falls back to absolute URL for development
const rawBaseUrl =
  typeof import.meta !== "undefined" && import.meta.env
    ? import.meta.env.VITE_API_BASE_URL || ""
    : "";

export const API_BASE_URL = rawBaseUrl.endsWith("/")
  ? rawBaseUrl.slice(0, -1)
  : rawBaseUrl;

export function buildApiUrl(path: string): string {
  // For consolidated deployment, use relative URLs
  if (!API_BASE_URL) {
    return path.startsWith("/") ? path : `/${path}`;
  }

  // Fallback to absolute URL for development
  if (!path.startsWith("/")) {
    return `${API_BASE_URL}/${path}`;
  }
  return `${API_BASE_URL}${path}`;
}

// ============================================================================
// API Endpoint Constants
// ============================================================================

export const API_ENDPOINTS = {
  // Core endpoints
  PATIENTS: "/patients",
  GLUCOSE: "/glucose",
  AGP: "/agp",
  INSIGHTS: "/insights",
  INFERENCE: "/inference",
  STORAGE: "/storage",
  NEO4J: "/neo4j",

  // D1NAMO extended endpoints
  INSULIN: "/insulin",
  ECG: "/ecg",
  MEAL_IMAGES: "/meal-images",
  PATIENT_PROFILES: "/patient-profiles",

  // Health and utilities
  HEALTH: "/health",
} as const;

export async function apiClient<TResponse>(
  path: string,
  options: ApiClientOptions = {},
): Promise<TResponse> {
  const { method = "GET", headers = {}, body, signal } = options;

  const response = await fetch(buildApiUrl(path), {
    method,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!response.ok) {
    const errorBody = await safeParseBody(response);
    throw new Error(
      errorBody?.error ||
        `API request failed (${response.status} ${response.statusText})`,
    );
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  return (await response.json()) as TResponse;
}

// ============================================================================
// Specialized API Clients for D1NAMO Data Types
// ============================================================================

/**
 * API client for insulin measurements with proper error handling
 */
export async function insulinApiClient<TResponse>(
  path: string,
  options: ApiClientOptions = {},
): Promise<TResponse> {
  return apiClient<TResponse>(`${API_ENDPOINTS.INSULIN}${path}`, {
    ...options,
    headers: {
      "X-Data-Type": "insulin-measurement",
      ...options.headers,
    },
  });
}

/**
 * API client for ECG features with proper error handling
 */
export async function ecgApiClient<TResponse>(
  path: string,
  options: ApiClientOptions = {},
): Promise<TResponse> {
  return apiClient<TResponse>(`${API_ENDPOINTS.ECG}${path}`, {
    ...options,
    headers: {
      "X-Data-Type": "ecg-features",
      ...options.headers,
    },
  });
}

/**
 * API client for meal images with support for base64 data
 */
export async function mealImageApiClient<TResponse>(
  path: string,
  options: ApiClientOptions = {},
): Promise<TResponse> {
  const { headers = {}, ...restOptions } = options;

  return apiClient<TResponse>(`${API_ENDPOINTS.MEAL_IMAGES}${path}`, {
    ...restOptions,
    headers: {
      "X-Data-Type": "meal-image",
      Accept: "application/json",
      ...headers,
    },
  });
}

/**
 * API client for patient profiles with comprehensive health data
 */
export async function patientProfileApiClient<TResponse>(
  path: string,
  options: ApiClientOptions = {},
): Promise<TResponse> {
  return apiClient<TResponse>(`${API_ENDPOINTS.PATIENT_PROFILES}${path}`, {
    ...options,
    headers: {
      "X-Data-Type": "patient-profile",
      ...options.headers,
    },
  });
}

async function safeParseBody(response: Response): Promise<any | null> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
